import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useModal } from '@context/ModalsContext'

import { Operator, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { AccountGroupType } from '@model/AccountGroupType'
import { PlatformTypeEnum } from '@model/PlatformTypeEnum'
import { TransferTypeEnum } from '@model/TransactionDto'
import { CurrencyType, WalletTypeEnum } from '@model/WalletDto'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'

import { TransferSuccessModal } from '../../modals/TransferSuccess.modal'
import {
  ConversionDirection,
  ConversionForm,
  ConversionType,
  CreateConversionFormSubmitValues,
  FetchRateOptions,
  PresetAccount,
} from './ConversionForm'

const useConversionFetch = () => {
  const apiClient = useApiClient(ClientApiClient)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.walletExtended.transferData({ type: 'conversion' }),
    queryFn: () =>
      Promise.all([
        apiClient.getWallets({
          walletTypeId: WalletTypeEnum.ETD,
        }),
        apiClient.getTransactionLimits(),
        apiClient.getTradingAccounts({
          pageSize: 150,
          search: {
            AccountGroupType: {
              value: AccountGroupType.Live,
              operator: Operator.EQUAL,
            },
            Platform: {
              value: PlatformTypeEnum.CQGCAST, // it's for ETD
              operator: Operator.EQUAL,
            },
            Status: {
              value: 1,
              operator: Operator.EQUAL,
            },
          },
        }),
        apiClient.getCurrencies(PlatformTypeEnum.CQGCAST),
      ]),
    refetchOnMount: 'always',
  })
  const [wallets = [], limits = [], tradingAccounts, availableTACurrencies = []] = data || []

  return {
    wallets,
    limits,
    tradingAccounts: tradingAccounts?.items || [],
    availableTACurrencies,
    isLoading,
  }
}

export const ConversionPage = () => {
  useScrollToTop()

  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state } = useLocation()
  const [tradingAccountId] = useState<string | undefined>(state?.tradingAccountId)
  const [currency] = useState<CurrencyType | undefined>(state?.currency)
  const [walletId] = useState<string | undefined>(state?.walletId)

  const [currentRate, setCurrentRate] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const transferSuccessModal = useModal(TransferSuccessModal, {
    isConversion: true,
    onClose: () => navigate('/dashboard/traders-room/transaction-history'),
  })

  const [rateLoading, setRateLoading] = useState(false)

  const {
    wallets,
    tradingAccounts,
    availableTACurrencies,
    limits,
    isLoading: isConversionLoading,
  } = useConversionFetch()

  const getPresetAccount = (): PresetAccount | undefined => {
    if (tradingAccountId && currency) {
      const account = tradingAccounts?.find((x) => x.id === tradingAccountId)
      if (account) {
        return {
          account,
          currency,
          type: ConversionType.TradingAccount,
          displayType: t('Trading Account.Trading Account'),
        }
      }
    }
    if (walletId) {
      const wallet = wallets.find((item) => item.id === walletId)
      if (wallet) {
        return {
          wallet,
          type: ConversionType.Wallet,
          displayType: t('Wallet.Wallet'),
        }
      }
    }
  }

  const onSubmit = async (values: CreateConversionFormSubmitValues) => {
    try {
      setIsLoading(true)
      if (values.fromType === 'wallet' && values.toType === 'wallet') {
        const isFrom = values.direction === ConversionDirection.From
        const conversionDirection = {
          [isFrom ? 'FromAmount' : 'ToAmount']: isFrom ? values.fromAmount : values.toAmount,
        }
        const masterTransaction = await apiClient.transferWalletToWallet({
          ...conversionDirection,
          caller: 'ca',
          FromWalletId: values.fromId,
          ToWalletId: values.toId,
          clientIpAddress: '',
          referrer_url: '',
        })
        transferSuccessModal.open({
          masterTransaction,
          transferType: TransferTypeEnum.WALLET_TO_WALLET,
        })
      } else if (
        values.fromType === 'tradingAccount' &&
        values.toType === 'tradingAccount' &&
        values.fromCurrencyId &&
        values.toCurrencyId
      ) {
        const isFrom = values.direction === ConversionDirection.From
        const conversionDirection = {
          [isFrom ? 'fromAmount' : 'toAmount']: isFrom ? values.fromAmount : values.toAmount,
        }
        const masterTransaction = await apiClient.convertTradingAccountBalance({
          ...conversionDirection,
          tradingAccountId: values.fromId || values.toId,
          fromCurrencyId: values.fromCurrencyId,
          toCurrencyId: values.toCurrencyId,
          comment: null,
        })

        queryClient.invalidateQueries({ queryKey: ['wallet'] })

        transferSuccessModal.open({ masterTransaction, transferType: TransferTypeEnum.TA_TO_TA })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const getRate = async (
    BaseCcy: CurrencyType,
    QuoteCcy: CurrencyType,
    options?: FetchRateOptions
  ) => {
    try {
      setRateLoading(true)
      const params = {
        BaseCcy,
        QuoteCcy,
        direction: 'sell' as const,
        ...(options?.BaseWalletTypeId !== undefined && options?.QuoteWalletTypeId !== undefined
          ? {
              BaseWalletTypeId: options.BaseWalletTypeId,
              QuoteWalletTypeId: options.QuoteWalletTypeId,
            }
          : {}),
      }
      const { rate } = await apiClient.getExchangeRate(params)
      setCurrentRate(rate)
    } finally {
      setRateLoading(false)
    }
  }

  return (
    <Loading showLoadingIcon isLoading={isLoading || isConversionLoading}>
      <ConversionForm
        limits={limits}
        rateLoading={rateLoading}
        onSubmit={onSubmit}
        fetchRate={getRate}
        exchangeRate={currentRate}
        wallets={wallets}
        tradingAccounts={tradingAccounts}
        availableTACurrencies={availableTACurrencies}
        fromAccount={getPresetAccount()}
      />
    </Loading>
  )
}
